import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, DollarSign, Users, TrendingUp, Clock, CheckCircle, 
    Link2, BarChart3, Gift, Zap, Star, ChevronRight, Loader2,
    Calculator, Target, Heart, Award, MousePointerClick, UserPlus,
    CreditCard, PiggyBank, Sparkles, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { affiliateApi } from '../lib/api';

const AffiliateLanding = ({ onNavigate }) => {
    const [showSignup, setShowSignup] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        website: '',
        marketing_channels: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    // Calculator state
    const [referrals, setReferrals] = useState(10);
    const [avgPlan, setAvgPlan] = useState(29);
    const [retention, setRetention] = useState(6);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.password) {
            toast.error('Please fill in all required fields');
            return;
        }
        setLoading(true);
        try {
            await affiliateApi.signup(formData);
            setSubmitted(true);
            toast.success('Application submitted successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit application');
        } finally {
            setLoading(false);
        }
    };

    // Calculate earnings
    const firstPaymentCommission = referrals * avgPlan * 0.30;
    const recurringCommission = referrals * avgPlan * 0.15 * (retention - 1);
    const totalEarnings = firstPaymentCommission + recurringCommission;
    const monthlyAvg = totalEarnings / retention;

    const benefits = [
        { icon: DollarSign, title: '30% First Payment', description: 'Earn 30% commission on every first payment from referred users' },
        { icon: TrendingUp, title: '15% Recurring', description: 'Continue earning 15% on all recurring payments' },
        { icon: Clock, title: '60-Day Cookies', description: 'Long attribution window ensures you get credit for referrals' },
        { icon: BarChart3, title: 'Real-Time Analytics', description: 'Track clicks, conversions, and earnings in your dashboard' },
        { icon: Link2, title: 'Custom Links', description: 'Create campaign-specific links to track performance' },
        { icon: Gift, title: 'Exclusive Perks', description: 'Top affiliates get early access to new features' },
    ];

    const steps = [
        { number: '1', title: 'Apply', description: 'Fill out the application form below. Takes just 2 minutes.' },
        { number: '2', title: 'Get Approved', description: 'Our team reviews applications within 24-48 hours.' },
        { number: '3', title: 'Share Your Link', description: 'Get your unique referral link and start sharing.' },
        { number: '4', title: 'Earn Money', description: 'Receive commissions via PayPal or bank transfer.' },
    ];

    const targetAudiences = [
        { icon: Star, title: 'Writing Bloggers', description: 'Share eSkrybe with your readers' },
        { icon: Users, title: 'Writing Coaches', description: 'Recommend to your clients' },
        { icon: Award, title: 'Book Reviewers', description: 'Help authors find the right tools' },
        { icon: Heart, title: 'Author Communities', description: 'Share with your writing groups' },
        { icon: Zap, title: 'Content Creators', description: 'Feature in videos or podcasts' },
        { icon: Target, title: 'Newsletter Owners', description: 'Promote to your subscribers' },
    ];

    const faqs = [
        { q: 'How much can I earn?', a: 'There is no cap on earnings. Top affiliates earn $1,000+ per month. With 30% on first payments and 15% recurring, even 10 referrals per month on our Author plan ($29/mo) equals $87+ first month, plus ongoing commissions.' },
        { q: 'When do I get paid?', a: 'Payments are processed monthly on the 15th for all commissions earned the previous month. Minimum payout threshold is $50.' },
        { q: 'How do I track my referrals?', a: 'Your affiliate dashboard shows real-time stats including clicks, signups, conversions, and earnings. You can also create custom campaign links to track different marketing channels.' },
        { q: 'What marketing materials do you provide?', a: 'While we don\'t host creative assets, you\'re welcome to use eSkrybe screenshots and create authentic content about your experience with the platform.' },
        { q: 'Can I be both a user and an affiliate?', a: 'Absolutely! Many of our best affiliates are active eSkrybe users who genuinely love the product and recommend it to fellow writers.' },
        { q: 'What are the program rules?', a: 'Be honest about your affiliate relationship, don\'t bid on eSkrybe branded terms in paid ads, and follow FTC disclosure guidelines. No spam or misleading claims.' },
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />
                        Back to eSkrybe
                    </button>
                    <div className="flex items-center gap-2">
                        <Link2 className="h-5 w-5 text-accent" />
                        <span className="font-semibold">Affiliate Program</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => onNavigate('affiliate-dashboard')}>
                        Affiliate Login
                    </Button>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-accent/10" />
                <div className="max-w-4xl mx-auto text-center relative">
                    <Badge className="mb-4 bg-accent/10 text-accent border-accent/20">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Join 500+ Affiliates
                    </Badge>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
                        Earn Money Helping<br />
                        <span className="text-accent">Writers Succeed</span>
                    </h1>
                    <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                        Join the eSkrybe affiliate program and earn up to 30% commission 
                        helping writers discover the ultimate AI-powered writing platform.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            size="lg" 
                            className="bg-accent hover:bg-accent/90 text-white"
                            onClick={() => setShowSignup(true)}
                            data-testid="apply-now-hero-btn"
                        >
                            Apply Now - It's Free
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button 
                            size="lg" 
                            variant="outline"
                            onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
                        >
                            Calculate Your Earnings
                        </Button>
                    </div>
                </div>
            </section>

            {/* Commission Stats */}
            <section className="py-12 px-6 bg-muted/30">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <Card className="text-center border-accent/20">
                            <CardContent className="pt-6">
                                <p className="text-4xl font-bold text-accent mb-2">30%</p>
                                <p className="text-sm text-muted-foreground">First Payment</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center border-accent/20">
                            <CardContent className="pt-6">
                                <p className="text-4xl font-bold text-accent mb-2">15%</p>
                                <p className="text-sm text-muted-foreground">Recurring</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center border-accent/20">
                            <CardContent className="pt-6">
                                <p className="text-4xl font-bold text-accent mb-2">60</p>
                                <p className="text-sm text-muted-foreground">Day Cookies</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center border-accent/20">
                            <CardContent className="pt-6">
                                <p className="text-4xl font-bold text-accent mb-2">$50</p>
                                <p className="text-sm text-muted-foreground">Min Payout</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section className="py-20 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-serif text-3xl font-semibold mb-4">Why Partner With eSkrybe?</h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Our affiliate program is designed to help you succeed with competitive commissions, 
                            powerful tracking tools, and reliable monthly payments.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit, i) => (
                            <Card key={i} className="hover:shadow-md transition-shadow">
                                <CardHeader>
                                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-3">
                                        <benefit.icon className="h-6 w-6 text-accent" />
                                    </div>
                                    <CardTitle className="text-lg">{benefit.title}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground">{benefit.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Earnings Calculator */}
            <section id="calculator" className="py-20 px-6 bg-muted/30">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-serif text-3xl font-semibold mb-4">Earnings Calculator</h2>
                        <p className="text-muted-foreground">
                            See how much you could earn as an eSkrybe affiliate
                        </p>
                    </div>
                    <Card>
                        <CardContent className="p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <Label className="mb-2 block">Monthly Referrals</Label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="range"
                                                min="1"
                                                max="100"
                                                value={referrals}
                                                onChange={(e) => setReferrals(parseInt(e.target.value))}
                                                className="flex-1 accent-accent"
                                            />
                                            <span className="text-2xl font-bold w-12 text-right">{referrals}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="mb-2 block">Average Plan Value</Label>
                                        <div className="flex gap-2">
                                            {[
                                                { label: 'Writer', value: 15 },
                                                { label: 'Author', value: 29 },
                                                { label: 'Studio', value: 79 },
                                            ].map((plan) => (
                                                <Button
                                                    key={plan.value}
                                                    variant={avgPlan === plan.value ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => setAvgPlan(plan.value)}
                                                    className={avgPlan === plan.value ? 'bg-accent' : ''}
                                                >
                                                    {plan.label} (${plan.value})
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="mb-2 block">Avg. Retention (months)</Label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="range"
                                                min="1"
                                                max="12"
                                                value={retention}
                                                onChange={(e) => setRetention(parseInt(e.target.value))}
                                                className="flex-1 accent-accent"
                                            />
                                            <span className="text-2xl font-bold w-12 text-right">{retention}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-center">
                                    <div className="bg-accent/10 rounded-xl p-6 text-center">
                                        <p className="text-sm text-muted-foreground mb-2">Estimated Earnings (over {retention} months)</p>
                                        <p className="text-5xl font-bold text-accent mb-4">${totalEarnings.toFixed(0)}</p>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p className="text-muted-foreground">First Month</p>
                                                <p className="font-semibold">${firstPaymentCommission.toFixed(0)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Monthly Avg</p>
                                                <p className="font-semibold">${monthlyAvg.toFixed(0)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-serif text-3xl font-semibold mb-4">How It Works</h2>
                        <p className="text-muted-foreground">
                            Getting started takes just a few minutes
                        </p>
                    </div>
                    <div className="grid md:grid-cols-4 gap-6">
                        {steps.map((step, i) => (
                            <div key={i} className="relative">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center text-white text-xl font-bold mb-4">
                                        {step.number}
                                    </div>
                                    <h3 className="font-semibold mb-2">{step.title}</h3>
                                    <p className="text-sm text-muted-foreground">{step.description}</p>
                                </div>
                                {i < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-[2px] bg-accent/20" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Perfect For */}
            <section className="py-20 px-6 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-serif text-3xl font-semibold mb-4">Perfect For</h2>
                        <p className="text-muted-foreground">
                            Our most successful affiliates come from these communities
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {targetAudiences.map((audience, i) => (
                            <Card key={i} className="hover:shadow-md transition-shadow">
                                <CardContent className="p-6 flex items-start gap-4">
                                    <div className="p-3 bg-accent/10 rounded-lg">
                                        <audience.icon className="h-5 w-5 text-accent" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">{audience.title}</h3>
                                        <p className="text-sm text-muted-foreground">{audience.description}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="py-20 px-6">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-serif text-3xl font-semibold mb-4">Frequently Asked Questions</h2>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, i) => (
                            <Card key={i}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-medium">{faq.q}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">{faq.a}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Application Form */}
            <section id="apply" className="py-20 px-6 bg-muted/30">
                <div className="max-w-xl mx-auto">
                    <Card>
                        <CardHeader className="text-center">
                            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                                <UserPlus className="h-8 w-8 text-accent" />
                            </div>
                            <CardTitle className="text-2xl">Apply to Join</CardTitle>
                            <CardDescription>
                                {submitted 
                                    ? 'Your application has been submitted!'
                                    : 'Fill out the form below to get started'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {submitted ? (
                                <div className="text-center py-8">
                                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold mb-2">Application Submitted!</h3>
                                    <p className="text-muted-foreground mb-6">
                                        We'll review your application and get back to you within 24-48 hours. 
                                        Check your email for confirmation.
                                    </p>
                                    <Button onClick={() => onNavigate('landing')}>
                                        Return to eSkrybe
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <Label htmlFor="name">Full Name *</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            data-testid="affiliate-apply-name"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email">Email Address *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            data-testid="affiliate-apply-email"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="password">Create Password *</Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            minLength={6}
                                            data-testid="affiliate-apply-password"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            You'll use this to access your affiliate dashboard
                                        </p>
                                    </div>
                                    <div>
                                        <Label htmlFor="website">Website or Social Media</Label>
                                        <Input
                                            id="website"
                                            value={formData.website}
                                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                            placeholder="https://yoursite.com"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="channels">How will you promote eSkrybe?</Label>
                                        <Input
                                            id="channels"
                                            value={formData.marketing_channels}
                                            onChange={(e) => setFormData({ ...formData, marketing_channels: e.target.value })}
                                            placeholder="Blog posts, YouTube videos, newsletter..."
                                        />
                                    </div>
                                    <Button 
                                        type="submit" 
                                        className="w-full bg-accent hover:bg-accent/90"
                                        disabled={loading}
                                        data-testid="affiliate-apply-submit"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                Submit Application
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-xs text-center text-muted-foreground">
                                        By applying, you agree to our affiliate terms and conditions.
                                    </p>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 px-6 bg-accent text-white">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="font-serif text-3xl font-semibold mb-4">
                        Ready to Start Earning?
                    </h2>
                    <p className="text-white/80 mb-8">
                        Join hundreds of affiliates earning passive income by recommending 
                        the writing tool authors love.
                    </p>
                    <Button 
                        size="lg" 
                        variant="secondary"
                        onClick={() => document.getElementById('apply').scrollIntoView({ behavior: 'smooth' })}
                        data-testid="final-cta-btn"
                    >
                        Apply Now - It's Free
                        <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-border">
                <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                        © 2026 eSkrybe, LLC. All rights reserved.
                    </p>
                    <div className="flex gap-6 text-sm text-muted-foreground">
                        <button onClick={() => onNavigate('terms')} className="hover:text-foreground">
                            Terms of Use
                        </button>
                        <button onClick={() => onNavigate('privacy')} className="hover:text-foreground">
                            Privacy Policy
                        </button>
                        <button onClick={() => onNavigate('contact')} className="hover:text-foreground">
                            Contact Us
                        </button>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AffiliateLanding;
